/**
 * Prompt templates for the selection → Ask Heidi actions (SCR selection menu).
 *
 * The highlighted note text is embedded in the outgoing chat message because
 * the chat/completions endpoint has no dedicated "selected text" context field
 * yet. Keeping every template here means a future backend context field only
 * requires swapping this layer.
 */

export type SelectionAiActionId =
  | 'rewrite'
  | 'make_shorter'
  | 'add_detail'
  | 'simplify_language'
  | 'check_guidelines'
  | 'view_source';

const ACTION_PROMPTS: Record<SelectionAiActionId, (text: string) => string> = {
  rewrite: (text) =>
    `Rewrite the following text from my note. Respond with the rewritten text only:\n\n"${text}"`,
  make_shorter: (text) =>
    `Make the following text from my note shorter while keeping its clinical meaning. Respond with the shortened text only:\n\n"${text}"`,
  add_detail: (text) =>
    `Add more clinically relevant detail to the following text from my note, drawing on the session context. Respond with the expanded text only:\n\n"${text}"`,
  simplify_language: (text) =>
    `Simplify the language of the following text from my note so it is easier to understand, while keeping it clinically accurate. Respond with the simplified text only:\n\n"${text}"`,
  check_guidelines: (text) =>
    `Check the following text from my note against relevant clinical guidelines and flag anything that deviates from them:\n\n"${text}"`,
  view_source: (text) =>
    `Show me where the following text from my note came from in this session. Quote the exact part(s) of the session transcript (or other session context) that informed it, and briefly explain how each quote supports it:\n\n"${text}"`,
};

/** Compose the outgoing chat message for a preset selection action. */
export const composeSelectionActionPrompt = (
  action: SelectionAiActionId,
  selectedText: string
): string => ACTION_PROMPTS[action](selectedText.trim());

/** Informational actions that can produce a key-points summary (v5 insights). */
export const SELECTION_INSIGHT_ACTION_IDS = ['check_guidelines', 'view_source'] as const;

export type SelectionInsightActionId = (typeof SELECTION_INSIGHT_ACTION_IDS)[number];

const KEY_POINTS_PROMPTS: Record<SelectionInsightActionId, (text: string) => string> = {
  check_guidelines: (text) =>
    `Check the following text from my note against relevant clinical guidelines. Respond with ONLY the 3–4 most important findings as short bullet points (each on its own line starting with "- "), no preamble or closing remarks:\n\n"${text}"`,
  view_source: (text) =>
    `Find where the following text from my note is supported in the session transcript. Respond with ONLY the 2–3 most relevant transcript quotes, one per line, each in exactly this format: [MM:SS] "exact quote" — where [MM:SS] is the timestamp of when it was said in the session. If no timestamp is available for a quote, write the line as just "exact quote". No preamble, numbering, or closing remarks:\n\n"${text}"`,
};

/**
 * Compose the short "key points" version of an informational action, shown in
 * the inline insight panel (v5). The full response lives in Evidence chat via
 * `composeSelectionActionPrompt` when the user asks to read more.
 */
export const composeSelectionKeyPointsPrompt = (
  action: SelectionInsightActionId,
  selectedText: string
): string => KEY_POINTS_PROMPTS[action](selectedText.trim());

/**
 * Wrap a free-text question typed in the floating bar so it applies to the
 * highlighted note text and nothing else.
 */
export const composeSelectedTextQuestionPrompt = (
  question: string,
  selectedText: string
): string =>
  `${question.trim()}\n\nApply this to the following selected text from my note, using it as the only note context:\n\n"${selectedText.trim()}"`;
